import React from 'react';
import PropTypes from 'prop-types';
import { alpha, Box, Typography } from '@mui/material';
import { C, DETAIL_CARD_SX, FONT_NUM } from '../tokens';
import {
  fmtNum,
  fmtPrice,
  getLocalizedText,
  regimeStyle,
  signalStyle,
  triggerStyle,
} from '../formatters';
import { Tag } from '../atoms';

// 6-cell grid of score / signal / trigger / regime / price / resistance.
const DetailScoreSignalCard = ({ detailDrawer, t, language, locale }) => (
  <Box sx={DETAIL_CARD_SX}>
    <Box sx={{
      px: 1.4,
      py: 1,
      bgcolor: alpha(C.accent, 0.035),
      borderBottom: `1px solid ${alpha(C.accent, 0.1)}`,
    }}>
      <Typography sx={{
        fontSize: '0.68rem',
        fontWeight: 800,
        color: '#334155',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}>
        {t('breakoutCandidates.drawer.score')} & {t('breakoutCandidates.drawer.signal')}
      </Typography>
    </Box>
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '1px',
      bgcolor: '#e2e8f0',
    }}>
      <Box sx={{ bgcolor: '#fff', px: 1.4, py: 1.1 }}>
        <Typography sx={{ fontSize: '0.58rem', color: C.mutedSoft, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
          {t('breakoutCandidates.drawer.score')}
        </Typography>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, color: C.ink, fontFamily: FONT_NUM }}>
          {fmtNum(detailDrawer.breakout_score, 1)}
        </Typography>
      </Box>
      <Box sx={{ bgcolor: '#fff', px: 1.4, py: 1.1 }}>
        <Typography sx={{ fontSize: '0.58rem', color: C.mutedSoft, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
          {t('breakoutCandidates.drawer.signal')}
        </Typography>
        <Box sx={{ mt: 0.35 }}>
          <Tag {...signalStyle(detailDrawer.signal)}>
            {getLocalizedText(detailDrawer.signal_label, language)}
          </Tag>
        </Box>
      </Box>
      <Box sx={{ bgcolor: '#fff', px: 1.4, py: 1.1 }}>
        <Typography sx={{ fontSize: '0.58rem', color: C.mutedSoft, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
          {t('breakoutCandidates.drawer.trigger')}
        </Typography>
        <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: triggerStyle(detailDrawer.trigger_type).fg }}>
          {getLocalizedText(detailDrawer.trigger_type_label, language)}
        </Typography>
      </Box>
      <Box sx={{ bgcolor: '#fff', px: 1.4, py: 1.1 }}>
        <Typography sx={{ fontSize: '0.58rem', color: C.mutedSoft, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
          {t('breakoutCandidates.drawer.regime')}
        </Typography>
        <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: regimeStyle(detailDrawer.market_regime).fg }}>
          {getLocalizedText(detailDrawer.market_regime_label, language)}
        </Typography>
      </Box>
      <Box sx={{ bgcolor: '#fff', px: 1.4, py: 1.1 }}>
        <Typography sx={{ fontSize: '0.58rem', color: C.mutedSoft, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
          {t('breakoutCandidates.drawer.price')}
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: C.ink, fontFamily: FONT_NUM }}>
          {fmtPrice(detailDrawer.price?.close, locale)}
        </Typography>
      </Box>
      <Box sx={{ bgcolor: '#fff', px: 1.4, py: 1.1 }}>
        <Typography sx={{ fontSize: '0.58rem', color: C.mutedSoft, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
          {t('breakoutCandidates.drawer.resistance20d')}
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: C.ink, fontFamily: FONT_NUM }}>
          {fmtPrice(detailDrawer.price?.resistance_20d, locale)}
        </Typography>
      </Box>
    </Box>
  </Box>
);

DetailScoreSignalCard.propTypes = {
  detailDrawer: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  t: PropTypes.func.isRequired,
  language: PropTypes.string,
  locale: PropTypes.string,
};

DetailScoreSignalCard.defaultProps = {
  language: 'en',
  locale: 'en-US',
};

export default DetailScoreSignalCard;
